/**
 * FFmpeg压缩器 - 专业级视频压缩
 * 
 * 特点：
 * ✅ 专业级压缩质量
 * ✅ 支持所有视频格式
 * ✅ 丰富的参数控制
 * ✅ 无需播放视频
 * ❌ 库文件大 (~20MB)
 * ❌ 加载时间长
 * ❌ 内存消耗大
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

interface FFmpegCompressOptions {
  crf?: number;           
  preset?: string;        
  resolution?: string;    
  bitrate?: string;       
  fps?: number;           
  codec?: string;         
  audioCodec?: string;    
  format?: string;        
  enableProgressLog?: boolean;
}

interface CompressResult {
  success: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  blob: Blob;
  processingTime: number;
  method: string;
  error?: string;
}

export class FFmpegCompressor {
  private static ffmpegInstance: any = null;
  private static isLoaded = false;
  private static workers: Worker[] = [];
  private static maxWorkers = 4; // 最大 Worker 数量

  /**
   * 检查FFmpeg.wasm支持
   */
  static isSupported(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * 初始化 Web Workers
   */
  private static async initializeWorkers(): Promise<void> {
    if (this.workers.length > 0) {
      return; // Workers 已经初始化
    }

    console.log('🚀 初始化 FFmpeg Web Workers...');
    
    for (let i = 0; i < this.maxWorkers; i++) {
      try {
        const worker = new Worker('/ffmpeg-worker.js');
        this.workers.push(worker);
        
        // 等待 Worker 准备就绪
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error(`Worker ${i} 初始化超时`));
          }, 10000);

          worker.onmessage = (event) => {
            if (event.data.type === 'WORKER_READY') {
              clearTimeout(timeout);
              resolve(true);
            }
          };

          worker.onerror = (error) => {
            clearTimeout(timeout);
            reject(error);
          };
        });

        console.log(`✅ Worker ${i + 1} 初始化完成`);
      } catch (error) {
        console.error(`❌ Worker ${i + 1} 初始化失败:`, error);
        throw new Error(`Web Workers 初始化失败: ${error instanceof Error ? error.message : '未知错误'}`);
      }
    }

    console.log(`🎉 所有 ${this.maxWorkers} 个 Workers 初始化完成`);
  }

  /**
   * 清理 Web Workers
   */
  private static cleanupWorkers(): void {
    this.workers.forEach((worker, index) => {
      try {
        worker.terminate();
        console.log(`🧹 Worker ${index + 1} 已清理`);
      } catch (error) {
        console.warn(`⚠️ Worker ${index + 1} 清理时出现警告:`, error);
      }
    });
    this.workers = [];
  }

  /**
   * 加载FFmpeg.wasm
   */
  static async loadFFmpeg(): Promise<void> {
    if (this.isLoaded && this.ffmpegInstance) {
      return;
    }

    try {
      console.log('📦 开始加载FFmpeg.wasm...');
      
      this.ffmpegInstance = new FFmpeg();

      console.log('⏳ 初始化FFmpeg...');
      await this.ffmpegInstance.load();
      
      this.isLoaded = true;
      console.log('✅ FFmpeg.wasm加载完成');
    } catch (error) {
      console.error('❌ FFmpeg.wasm加载失败:', error);
      throw new Error('FFmpeg.wasm加载失败，请检查网络连接');
    }
  }

  /**
   * 使用 Web Workers 进行并行压缩
   */
  static async compressWithWorkers(
    file: File,
    options: FFmpegCompressOptions = {},
    progressCallback?: (progress: number) => void
  ): Promise<CompressResult> {
    const startTime = Date.now();

    if (!this.isSupported()) {
      throw new Error('当前环境不支持FFmpeg.wasm');
    }

    const {
      crf = 28,
      preset = 'fast',
      resolution,
      bitrate,
      fps,
      codec = 'libx264',
      audioCodec = 'aac',
      format = 'mp4',
      enableProgressLog = true
    } = options;

    try {
      // 初始化 Workers
      await this.initializeWorkers();

      if (enableProgressLog) {
        console.log('🚀 FFmpeg Web Workers 并行压缩开始:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          质量参数: `CRF ${crf}`,
          压缩预设: preset,
          目标分辨率: resolution || '原始',
          比特率: bitrate || '自动',
          帧率: fps || '原始',
          视频编码: codec,
          音频编码: audioCodec,
          输出格式: format,
          Worker数量: this.maxWorkers
        });
      }

      // 获取视频时长
      const videoDuration = await this.getVideoDuration(file);
      const segmentDuration = Math.max(2, videoDuration / this.maxWorkers); // 每段至少2秒
      const segments = this.createVideoSegments(videoDuration, segmentDuration);

      if (enableProgressLog) {
        console.log(`📊 视频分段信息:`, {
          总时长: `${videoDuration.toFixed(2)}秒`,
          分段数量: segments.length,
          每段时长: `${segmentDuration.toFixed(2)}秒`
        });
      }

      // 准备文件数据
      const fileData = await fetchFile(file);

      // 创建分段任务
      const segmentTasks = segments.map((segment, index) => ({
        segmentData: {
          segmentIndex: index,
          totalSegments: segments.length,
          startTime: segment.start,
          duration: segment.duration,
          fileData: fileData,
          fileName: file.name
        },
        options: { crf, preset, resolution, bitrate, fps, codec, audioCodec, format }
      }));

      // 并行处理所有分段
      const segmentResults = await this.processSegmentsInParallel(
        segmentTasks,
        progressCallback
      );

      // 检查是否有失败的分段
      const failedSegments = segmentResults.filter(result => !result.success);
      if (failedSegments.length > 0) {
        throw new Error(`有 ${failedSegments.length} 个分段处理失败`);
      }

      // 合并分段结果
      const finalBlob = await this.mergeVideoSegments(segmentResults, format);

      const processingTime = Date.now() - startTime;

      const result: CompressResult = {
        success: true,
        originalSize: file.size,
        compressedSize: finalBlob.size,
        compressionRatio: finalBlob.size / file.size,
        blob: finalBlob,
        processingTime,
        method: 'FFmpeg Web Workers 并行压缩'
      };

      if (enableProgressLog) {
        console.log('✅ FFmpeg Web Workers 并行压缩完成:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩后大小: `${(finalBlob.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩率: `${((1 - result.compressionRatio) * 100).toFixed(1)}%`,
          处理时间: `${(processingTime / 1000).toFixed(1)}秒`,
          分段数量: segments.length,
          平均每段时间: `${(processingTime / segments.length / 1000).toFixed(1)}秒`
        });
      }

      return result;

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error('❌ FFmpeg Web Workers 并行压缩失败:', error);
      
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime,
        method: 'FFmpeg Web Workers 并行压缩',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * FFmpeg专业压缩（原始方法）
   */
  static async compress(
    file: File,
    options: FFmpegCompressOptions = {},
    progressCallback?: (progress: number) => void
  ): Promise<CompressResult> {
    const startTime = Date.now();

    if (!this.isSupported()) {
      throw new Error('当前环境不支持FFmpeg.wasm');
    }

    const {
      crf = 28,
      preset = 'fast',
      resolution,
      bitrate,
      fps,
      codec = 'libx264',
      audioCodec = 'aac',
      format = 'mp4',
      enableProgressLog = true
    } = options;

    try {
      // 加载FFmpeg
      await this.loadFFmpeg();

      if (enableProgressLog) {
        console.log('🚀 FFmpeg压缩开始:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          质量参数: `CRF ${crf}`,
          压缩预设: preset,
          目标分辨率: resolution || '原始',
          比特率: bitrate || '自动',
          帧率: fps || '原始',
          视频编码: codec,
          音频编码: audioCodec,
          输出格式: format
        });
      }

      // 准备输入文件
      const inputName = `input.${this.getFileExtension(file.name)}`;
      const outputName = `output.${format}`;
      
      // 写入输入文件 (v0.12+ API)
      await this.ffmpegInstance.writeFile(inputName, await fetchFile(file));

      // 构建FFmpeg命令
      const command = this.buildFFmpegCommand(
        inputName, 
        outputName, 
        { crf, preset, resolution, bitrate, fps, codec, audioCodec }
      );

      if (enableProgressLog) {
        console.log('🛠️ FFmpeg命令:', command.join(' '));
      }

      // 执行压缩 (v0.12+ 使用 exec 方法)
      if (progressCallback) {
        // 模拟进度更新
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const estimatedTotal = Math.max(10000, file.size / 1000); // 估算总时间
          const progress = Math.min(90, (elapsed / estimatedTotal) * 100);
          progressCallback(progress);
        }, 500);
        
        try {
          await this.ffmpegInstance.exec(command);
          clearInterval(progressInterval);
          progressCallback(100);
        } catch (error) {
          clearInterval(progressInterval);
          throw error;
        }
      } else {
        await this.ffmpegInstance.exec(command);
      }

      // 读取输出文件 (v0.12+ API)
      const outputData = await this.ffmpegInstance.readFile(outputName);
      const compressedBlob = new Blob([outputData], { 
        type: this.getMimeType(format) 
      });

      // 清理临时文件 (v0.12+ API)
      await this.ffmpegInstance.deleteFile(inputName);
      await this.ffmpegInstance.deleteFile(outputName);

      const processingTime = Date.now() - startTime;

      const result: CompressResult = {
        success: true,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        compressionRatio: compressedBlob.size / file.size,
        blob: compressedBlob,
        processingTime,
        method: 'FFmpeg专业压缩'
      };

      if (enableProgressLog) {
        console.log('✅ FFmpeg压缩完成:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩后大小: `${(compressedBlob.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩率: `${((1 - result.compressionRatio) * 100).toFixed(1)}%`,
          处理时间: `${(processingTime / 1000).toFixed(1)}秒`
        });
      }

      return result;

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error('❌ FFmpeg压缩失败:', error);
      
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime,
        method: 'FFmpeg专业压缩',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 构建FFmpeg命令
   */
  private static buildFFmpegCommand(
    inputName: string,
    outputName: string,
    options: {
      crf: number;
      preset: string;
      resolution?: string;
      bitrate?: string;
      fps?: number;
      codec: string;
      audioCodec: string;
    }
  ): string[] {
    const command = [
      '-i', inputName,
      '-c:v', options.codec,
      '-crf', options.crf.toString(),
      '-preset', options.preset,
      '-c:a', options.audioCodec
    ];

    // 添加分辨率设置
    if (options.resolution) {
      command.push('-s', options.resolution);
    }

    // 添加比特率设置
    if (options.bitrate) {
      command.push('-b:v', options.bitrate);
    }

    // 添加帧率设置
    if (options.fps) {
      command.push('-r', options.fps.toString());
    }

    // 添加优化参数
    command.push(
      '-movflags', '+faststart',  // 优化流媒体播放
      '-y',                       // 覆盖输出文件
      outputName
    );

    return command;
  }

  /**
   * 智能压缩预设
   */
  static async compressWithPreset(
    file: File,
    preset: 'ultrafast' | 'fast' | 'balanced' | 'quality' | 'small_size',
    targetSizeMB?: number
  ): Promise<CompressResult> {
    const fileSizeMB = file.size / (1024 * 1024);
    let options: FFmpegCompressOptions;

    switch (preset) {
      case 'ultrafast':
        options = {
          crf: 35,
          preset: 'ultrafast',
          resolution: '854x480',
          fps: 24
        };
        break;
      
      case 'fast':
        options = {
          crf: 30,
          preset: 'fast',
          resolution: '1280x720',
          fps: 30
        };
        break;
      
      case 'balanced':
        options = {
          crf: 28,
          preset: 'medium',
          fps: 30
        };
        break;
      
      case 'quality':
        options = {
          crf: 23,
          preset: 'slow',
          fps: 30
        };
        break;
      
      case 'small_size':
        options = {
          crf: 32,
          preset: 'veryslow',
          resolution: fileSizeMB > 100 ? '854x480' : undefined,
          fps: 24,
          bitrate: '500k'
        };
        break;
      
      default:
        options = { crf: 28, preset: 'fast' };
    }

    console.log(`🎯 使用${preset}预设压缩`);
    return this.compress(file, options);
  }

  /**
   * 批量压缩
   */
  static async compressBatch(
    files: File[],
    options: FFmpegCompressOptions = {}
  ): Promise<CompressResult[]> {
    const results: CompressResult[] = [];
    
    console.log(`🚀 开始批量FFmpeg压缩 ${files.length} 个文件`);
    
    // 预加载FFmpeg
    await this.loadFFmpeg();
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`⏳ 处理第 ${i + 1}/${files.length} 个文件: ${file.name}`);
      
      const result = await this.compress(file, {
        ...options,
        enableProgressLog: false
      });
      
      results.push(result);
      
      console.log(`${result.success ? '✅' : '❌'} 第 ${i + 1} 个文件处理完成`);
    }
    
    console.log('🎉 批量压缩完成');
    return results;
  }

  /**
   * 获取文件扩展名
   */
  private static getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || 'mp4';
  }

  /**
   * 获取MIME类型
   */
  private static getMimeType(format: string): string {
    const mimeTypes: { [key: string]: string } = {
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'avi': 'video/avi',
      'mov': 'video/quicktime',
      'mkv': 'video/x-matroska'
    };
    
    return mimeTypes[format] || 'video/mp4';
  }



  /**
   * 获取视频时长
   */
  private static async getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve(video.duration);
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        reject(new Error('无法获取视频时长'));
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * 创建视频分段
   */
  private static createVideoSegments(duration: number, segmentDuration: number): Array<{start: number, duration: number}> {
    const segments = [];
    let currentTime = 0;
    
    while (currentTime < duration) {
      const remainingTime = duration - currentTime;
      const currentSegmentDuration = Math.min(segmentDuration, remainingTime);
      
      segments.push({
        start: currentTime,
        duration: currentSegmentDuration
      });
      
      currentTime += currentSegmentDuration;
    }
    
    return segments;
  }

  /**
   * 并行处理分段
   */
  private static async processSegmentsInParallel(
    segmentTasks: Array<{segmentData: any, options: any}>,
    progressCallback?: (progress: number) => void
  ): Promise<Array<any>> {
    let completedSegments = 0;
    
    // 创建 Promise 数组，每个 Worker 处理一个分段
    const promises = segmentTasks.map((task, index) => {
      const worker = this.workers[index % this.workers.length];
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`分段 ${index} 处理超时`));
        }, 60000); // 60秒超时
        
        const messageHandler = (event: MessageEvent) => {
          if (event.data.type === 'SEGMENT_COMPLETE') {
            clearTimeout(timeout);
            worker.removeEventListener('message', messageHandler);
            
            completedSegments++;
            if (progressCallback) {
              const progress = (completedSegments / segmentTasks.length) * 100;
              progressCallback(progress);
            }
            
            resolve(event.data.data);
          } else if (event.data.type === 'ERROR') {
            clearTimeout(timeout);
            worker.removeEventListener('message', messageHandler);
            reject(new Error(event.data.data.error));
          }
        };
        
        worker.addEventListener('message', messageHandler);
        worker.postMessage({
          type: 'PROCESS_SEGMENT',
          data: task
        });
      });
    });
    
    // 等待所有分段完成
    const segmentResults = await Promise.all(promises);
    return segmentResults;
  }

  /**
   * 合并视频分段
   */
  private static async mergeVideoSegments(segmentResults: Array<any>, format: string): Promise<Blob> {
    // 简单合并：将所有分段的 Blob 连接起来
    // 注意：这是一个简化的实现，实际应用中可能需要更复杂的合并逻辑
    
    const blobs = segmentResults
      .filter(result => result.success)
      .sort((a, b) => a.segmentIndex - b.segmentIndex)
      .map(result => result.blob);
    
    if (blobs.length === 0) {
      throw new Error('没有成功处理的分段');
    }
    
    // 合并所有 Blob
    const mergedBlob = new Blob(blobs, { type: this.getMimeType(format) });
    
    console.log(`🔗 合并了 ${blobs.length} 个分段`);
    return mergedBlob;
  }

  /**
   * 清理FFmpeg实例
   */
  static cleanup(): void {
    // 清理 Workers
    this.cleanupWorkers();
    
    if (this.ffmpegInstance && this.isLoaded) {
      try {
        // FFmpeg.wasm没有明确的cleanup方法，只需要重置状态
        this.isLoaded = false;
        this.ffmpegInstance = null;
        console.log('🧹 FFmpeg实例已清理');
      } catch (error) {
        console.warn('⚠️ FFmpeg清理时出现警告:', error);
      }
    }
  }

  /**
   * 获取FFmpeg信息
   */
  static async getFFmpegInfo(): Promise<{
    loaded: boolean;
    version?: string;
    supportedCodecs?: string[];
  }> {
    if (!this.isLoaded) {
      return { loaded: false };
    }

    // FFmpeg.wasm的版本信息
    return {
      loaded: true,
      version: '4.4.0 (FFmpeg.wasm)',
      supportedCodecs: [
        'libx264', 'libx265', 'vp8', 'vp9', 
        'aac', 'mp3', 'opus', 'vorbis'
      ]
    };
  }
} 